import { Table, Select } from "antd";
import React, { useMemo, useEffect, useCallback } from "react";
import {
  LoadingOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
} from "@ant-design/icons";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import { EmptyResultExplanation } from "./EmptyResultExplanation";
import {
  TableHeaderCustomComponent,
  TableHeaderProvider,
} from "./table/TableHeader";
import { VisualizationProps } from "./visualization-props";
import { Flex } from "src/components/Flex";
import { Switch } from "antd";
import { TableLayoutSelector } from "./UnifiedTableVisualization";
import { ContentViews } from "./useContentView";
import { useSubscribeEffect } from "src/hooks/useSubscribeEffect";
import { Field, Aggregation, ColumnFormula } from "src/search/types";
import { TableCell } from "./table/cells/TableCell";
import { CellFormatterOptions } from "./table/formatters/types";
import {
  CompareWithConfig,
  CompareWithControls,
  toCompareWithConfig,
} from "./CompareWithControls";
import {
  FormattingConfig,
  FormattingControls,
  hasTimeUnit,
  toFormattingConfig,
} from "./FormattingControls";
import { usePivotTableData } from "./hooks/usePivotTableData";
import { GroupedTableHeader } from "./table/TableHeader";
import { extractField, formatTitle } from "src/utils/strings";
import { VZBLTooltip, TableColumnHeaderTooltip } from "./tooltips/VZBLTooltips";
import { ValueFormatter } from "src/utils/formatters";
import { Api } from "@vzbl/integrations";
import { PivotTableConfig } from "./hooks/usePivotTableData";
import { AggregationConfig } from "./types";
import { TableColumns } from "./TableColumns";
import { debounce } from "lodash";
import styles from "./NestedGroupedTable.module.css";
import { isComparisonValue } from "./hooks/useComparisonValue";
import { formatValue } from "src/utils/strings";

// Shared sorting utilities
function createSortHandler(sortKey: string, config: any, context: any) {
  return () => {
    if (!config.enableSorting) return;

    let newDirection: "ascending" | "descending" | undefined;
    if (config.sortBy === sortKey) {
      // Currently sorting by this column, toggle direction
      if (config.sortDirection === "ascending") {
        newDirection = "descending";
      } else if (config.sortDirection === "descending") {
        newDirection = undefined; // Remove sorting
      } else {
        newDirection = "ascending";
      }
    } else {
      // Not currently sorting by this column, start with ascending
      newDirection = "ascending";
    }

    context.view.setConfig({
      ...config,
      sortBy: newDirection ? sortKey : undefined,
      sortDirection: newDirection,
    });
  };
}

interface SortableColumnHeaderProps {
  config: any;
  sortKey: string;
  children: React.ReactNode;
  onClick: () => void;
}

function SortableColumnHeader({
  config,
  sortKey,
  children,
  onClick,
}: SortableColumnHeaderProps) {
  const isCurrentSort = config.enableSorting && config.sortBy === sortKey;
  const sortDirection = isCurrentSort ? config.sortDirection : undefined;

  const renderSortIcon = () => {
    if (!config.enableSorting) return null;

    const iconStyle = {
      fontSize: "12px",
      color: isCurrentSort ? "#1890ff" : "#bfbfbf",
      marginLeft: "4px",
    };

    if (sortDirection === "ascending") {
      return <CaretUpOutlined style={iconStyle} />;
    } else if (sortDirection === "descending") {
      return <CaretDownOutlined style={iconStyle} />;
    } else {
      // Show both arrows stacked when not sorted (similar to ↕)
      return (
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            lineHeight: "6px",
            marginLeft: "4px",
          }}
        >
          <CaretUpOutlined style={{ ...iconStyle, fontSize: "8px" }} />
          <CaretDownOutlined style={{ ...iconStyle, fontSize: "8px" }} />
        </span>
      );
    }
  };

  return (
    <div
      style={{
        cursor: config.enableSorting ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
      onClick={onClick}
    >
      {children}
      {renderSortIcon()}
    </div>
  );
}

/**
 * TODO: this code is a mess, i need to refactor it to be more readable and maintainable
 * I had to go fast and just get it working, so i'm sorry for the mess.
 */
export interface PivotTableV2Config
  extends CompareWithConfig,
    FormattingConfig {
  rowGroups?: Field[];
  columnGroups?: Field[];
  aggregations?: AggregationConfig[];
  columnInfo?: Api.Schema.ColumnInfo[];
  measuresAsRows?: boolean;
  pivotGroupIndex?: number;
  rollupMeasureKey?: string;
  showRollups?: boolean;
  autoPageSize?: boolean;
  allowWrap?: boolean;
  columns?: string[]; // Selected columns to display
  // Toggle for showing rollup summaries
  // Sorting configuration
  sortBy?: string;
  sortDirection?: "ascending" | "descending";
  enableSorting?: boolean;
}

export interface PivotTableV2ControlsConfig {
  showGroupSelector?: boolean;
  showPaging?: boolean;
  showWrapping?: boolean;
  showCompare?: boolean;
  showLayoutSelector?: boolean;
  showColumnSelector?: boolean;
  showRollupOptions?: boolean;
  showRollupMeasureSelector?: boolean;
  showSortingControls?: boolean;
}

// Helper function to get the path for a record
function getRecordPath(record: any): string[] {
  const path = [];
  let current = record;
  while (current) {
    if (current.value) {
      path.unshift(current.value);
    }
    current = current.parent;
  }
  return path;
}

// Build the base group column that's always present
function buildGroupColumn(
  config: any,
  aggregations: any[],
  columnInfo: Api.Schema.ColumnInfo,
  context: any,
  shouldFlatten: boolean
) {
  const showRollups = context.view.config?.showRollups ?? true;
  return {
    title: (props: any) => {
      // Get the group field information from the context
      const field = context.params.state.groupBys?.[0];
      const handleSort = createSortHandler("value", config, context);

      return (
        <SortableColumnHeader
          config={config}
          sortKey="value"
          onClick={handleSort}
        >
          <GroupedTableHeader
            field={field}
            index={0}
            params={context.params}
            showMovers={false}
            columnInfo={columnInfo}
          />
        </SortableColumnHeader>
      );
    },
    dataIndex: "value",
    key: "value",
    fixed: "left" as const,
    className: styles.resizableGroupColumn,
    render: (value: any, record: any) => {
      let isParentRow = record.children?.length > 0;

      // If we should flatten and this is a parent row with one child, treat as leaf
      if (shouldFlatten && isParentRow && record.children?.length === 1) {
        isParentRow = false;
      }

      //todo this should go into a formatter for the tooltips.
      // Get the group field information from the record to use for formatting
      const groupField =
        record.groupBys?.[record.depth] ||
        context.params.state.groupBys?.[record.depth];

      return (
        <TableCell
          value={value}
          options={{
            type: "group",
            showTooltip: true,
            allowWrap: false,
          }}
          record={record}
          isExpandable={!shouldFlatten && record.children?.length > 0}
          isExpanded={record.expanded}
          onExpand={() => record.onExpand?.(!record.expanded, record)}
          level={record.depth || 0}
          context={context}
          properties={[
            {
              target: groupField?.target || record.target,
              attribute: groupField?.attribute || record.attribute,
            },
          ]}
          rowCount={isParentRow ? record.children?.length : undefined}
          showRollup={showRollups}
          renderTooltip={(content) => (
            <VZBLTooltip
              type="group"
              value={value}
              formattedValue={value}
              record={record}
              metadata={{
                level: record.depth || 0,
                path: getRecordPath(record),
                depth: record.depth,
              }}
              aggregations={aggregations}
              currentColumn={groupField}
              groupBys={record.groupBys}
            >
              {content}
            </VZBLTooltip>
          )}
        />
      );
    },
  };
}

// Build measure columns when no pivot group is selected
function buildMeasureColumns(
  aggregations: any[],
  context: any,
  columnInfo: Api.Schema.ColumnInfo
) {
  return aggregations.map((agg, index) => {
    const field =
      agg.reducer === "formula"
        ? { target: "formula", attribute: agg.field }
        : extractField(agg.field);
    const title =
      agg.reducer === "formula" ? agg.title : formatTitle(agg.field);

    const { unit } = columnInfo.find((c) => c.key === agg.key) || {
      unit: undefined,
    };

    return {
      title: (props: any) => {
        const config = context.view.config || {};
        const handleSort = createSortHandler(agg.key, config, context);

        return (
          <SortableColumnHeader
            config={config}
            sortKey={agg.key}
            onClick={handleSort}
          >
            <TableColumnHeaderTooltip
              field={field}
              title={title}
              type={agg.reducer === "formula" ? "formula" : "aggregation"}
              aggregation={agg}
            />
          </SortableColumnHeader>
        );
      },
      dataIndex: ["measures", agg.key],
      key: agg.key,
      render: (value: any, record: any) => {
        const isParent = record.children?.length > 0;

        // Check if this is a comparison value
        const isComparison = isComparisonValue(value);
        if (isComparison) {
          const options: CellFormatterOptions = {
            type: "comparison",
            field: field.attribute,
            showTooltip: false,
            style: "human",
            columnKey: agg.key,
            unit: unit,
            showChangeAs: context.view.config?.showChangeAs,
            showChangeValues: context.view.config?.showChangeValues,
            changeColor: context.view.config?.changeColor,
          };

          return <TableCell value={value} options={options} record={record} />;
        }

        const options: CellFormatterOptions = {
          type: isParent ? "measure-group" : "measure",
          field: field.attribute,
          showTooltip: false,
          style: "human",
          columnKey: agg.key,
          unit: unit,
          toUnit: context.view.config?.timeUnit,
          showChangeAs: context.view.config?.showChangeAs,
          showChangeValues: context.view.config?.showChangeValues,
          changeColor: context.view.config?.changeColor,
          showRollup: context.view.config?.showRollups,
          selectedRollupStats: context.view.config?.selectedRollupStats,
        };

        return (
          <VZBLTooltip
            type={isParent ? "measure-group" : "measure"}
            field={field}
            value={value}
            formattedValue={ValueFormatter.format(value, {
              field: field.attribute,
              unit: unit,
            })}
            record={record}
            currentColumn={{
              measureKey: agg.key,
              rollupDetails: record.rollupDetails?.[agg.key],
              formula: agg.formula,
            }}
            metadata={{
              level: record.depth || 0,
              path: getRecordPath(record),
              isLeaf: !record.children?.length,
              depth: record.depth,
              isFormula: agg.reducer === "formula",
            }}
            aggregations={[agg]}
          >
            <TableCell
              value={value}
              options={options}
              record={{
                ...record,
                measureField: agg.field,
                measureName: agg.field,
                formula: agg.formula,
              }}
              context={context}
              properties={[
                {
                  target: field.target,
                  attribute: field.attribute,
                },
              ]}
            />
          </VZBLTooltip>
        );
      },
    };
  });
}

// Build pivot columns when a pivot group is selected
function buildPivotColumns(
  columnKeys: string[],
  aggregations: AggregationConfig[],
  pivotGroups: Field[],
  columnInfo: Api.Schema.ColumnInfo,
  context: any,
  shouldFlatten: boolean,
  rollupMeasureKey: string | undefined
) {
  const showRollups = context.view.config?.showRollups ?? false;

  return columnKeys.map((columnKey, index) => {
    const pivotGroup = pivotGroups[0] || { target: "", attribute: "" };

    // Format column header to handle ww/yy to mm/dd/yy transformation
    const fieldKey = `${pivotGroup.target}.${pivotGroup.attribute}`;
    const columnMeta = columnInfo.find((c) => c.key === fieldKey);
    const title = columnMeta?.unit
      ? formatValue(columnKey, columnMeta.type, columnMeta.unit)
      : columnKey;

    return {
      title: (props: any) => {
        const config = context.view.config || {};
        const handleSort = createSortHandler(columnKey, config, context);

        return (
          <SortableColumnHeader
            config={config}
            sortKey={columnKey}
            onClick={handleSort}
          >
            <GroupedTableHeader
              field={pivotGroup}
              index={index}
              params={context.params}
              title={title}
              showMovers={false}
              columnInfo={columnInfo}
            />
          </SortableColumnHeader>
        );
      },
      dataIndex: ["measures", columnKey],
      key: columnKey,
      render: (value: any, record: any) => {
        let isParentRow = record.children?.length > 0;
        let field = record.field;
        let measureKey = field.measureKey;

        // If we should flatten and this is a parent row, show the child's value
        if (shouldFlatten && record.children?.length === 1) {
          field = record.children[0].field;
          value = record.children[0].measures[columnKey];
          measureKey = field.measureKey;
          isParentRow = false; // Treat as a leaf node for rendering
          // use the rollup measure key if it exists and there are multiple children
        } else if (rollupMeasureKey && record.children?.length > 0) {
          measureKey = rollupMeasureKey;
        }

        const agg = aggregations.find((agg) => agg.key === measureKey);
        // Look up the unit using the original measure key
        const { unit } = columnInfo.find((c) => c.key === agg?.key) || {
          unit: undefined,
        };

        const options: CellFormatterOptions = {
          type: isParentRow
            ? "pivot-parent"
            : isComparisonValue(value)
            ? "comparison"
            : "measure",
          field: field?.attribute,
          showTooltip: false,
          style: "human",
          columnKey,
          showRollup: showRollups,
          unit: unit,
          toUnit: context.view.config?.timeUnit,
          showChangeAs: context.view.config?.showChangeAs,
          showChangeValues: context.view.config?.showChangeValues,
          changeColor: context.view.config?.changeColor,
          selectedRollupStats: context.view.config?.selectedRollupStats,
        };

        return (
          <VZBLTooltip
            type={isParentRow ? "group" : "measure"}
            record={record}
            field={field}
            value={value}
            formattedValue={ValueFormatter.format(value, {
              field: field?.attribute,
              unit: unit,
            })}
            pivotGroups={[pivotGroup]}
            currentColumn={{
              measureKey,
              rollupDetails:
                isParentRow && showRollups
                  ? record.rollupDetails?.[columnKey]?.[measureKey]
                  : undefined,
              formula: agg?.formula,
            }}
            metadata={{
              level: record.depth || 0,
              path: getRecordPath(record),
              isLeaf: !record.children?.length || shouldFlatten,
              depth: record.depth,
              isFormula: agg?.reducer === "formula",
            }}
            aggregations={(() => {
              const agg = aggregations.find((agg) => agg.key === measureKey);
              return agg ? [agg] : [];
            })()}
          >
            <TableCell
              value={value}
              options={options}
              record={{
                ...record,
                measureField: record.measureName,
                measureName: record.value,
                formula: agg?.formula,
              }}
              context={context}
              properties={[
                {
                  target: record.target,
                  attribute: record.attribute,
                },
              ]}
              isExpandable={!shouldFlatten && record.children?.length > 0}
              isExpanded={record.expanded}
              onExpand={() => record.onExpand?.(!record.expanded, record)}
              level={record.depth || 0}
              showRollup={showRollups}
            />
          </VZBLTooltip>
        );
      },
    };
  });
}

function buildColumns(
  columnKeys: string[],
  config: any,
  aggregations: any[],
  pivotGroup: Field | undefined,
  columnInfo: Api.Schema.ColumnInfo,
  context: any,
  shouldFlatten: boolean,
  rollupMeasureKey: string | undefined
) {
  const baseColumns = [
    buildGroupColumn(config, aggregations, columnInfo, context, shouldFlatten),
  ];
  // Filter columns based on selected columns if specified

  return pivotGroup
    ? [
        ...baseColumns,
        ...buildPivotColumns(
          columnKeys,
          aggregations,
          [pivotGroup],
          columnInfo,
          context,
          shouldFlatten,
          rollupMeasureKey
        ),
      ]
    : [
        ...baseColumns,
        // Filter and reorder measure columns based on selected columns if specified
        ...(() => {
          const builtColumns = buildMeasureColumns(
            aggregations,
            context,
            columnInfo
          );
          if (!config.columns) return builtColumns;

          // Reorder columns based on config.columns order
          return config.columns
            .map((colKey: string) =>
              builtColumns.find((col: any) => col.key === colKey)
            )
            .filter(Boolean);
        })(),
      ];
}

function buildAllColumns(
  aggregations: Aggregation[] | undefined,
  formulas: ColumnFormula[] | undefined
): string[] {
  return [
    ...(aggregations || []).map(
      (agg) => `${agg.reducer}(${agg.target}.${agg.attribute})`
    ),
    ...(formulas || []).map((formula) => formula.name),
  ];
}

export function PivotTableV2({
  context,
  config,
}: {
  context: VisualizationProps["context"];
  config: PivotTableV2Config;
}) {
  const { aggregations } = context;
  useSubscribeEffect(aggregations);

  // Select all columns if none are selected
  useEffect(() => {
    // If we're in nested mode and no columns are selected, select all columns
    if (!config.columns?.length && context.view.config?.layout === "nested") {
      const allColumns = buildAllColumns(
        context.params.state.aggregations,
        context.params.state.formulas
      );
      if (allColumns.length > 0) {
        context.view.setConfig({ ...config, columns: allColumns });
      }
    }
  }, [
    config.columns,
    context.params.state.aggregations,
    context.params.state.formulas,
  ]);

  // Configure pivot table
  const groupBys = context.params.state.groupBys || [];
  const pivotGroupIndex = config.pivotGroupIndex;

  let rowGroups = [...groupBys];
  let columnGroups: Field[] = [];

  if (pivotGroupIndex !== undefined && pivotGroupIndex < groupBys.length) {
    const pivotGroup = rowGroups.splice(pivotGroupIndex, 1)[0];
    columnGroups = [pivotGroup];

    rowGroups.push({
      target: "measure",
      attribute: "name",
      type: "string",
    } as Field);
  }
  const pivotConfig: PivotTableConfig = {
    rowGroups,
    columnGroups,
    aggregations: [
      ...(context.params.state.aggregations || []).map((agg) => ({
        type: "value" as const,
        field: `${agg.target}.${agg.attribute}`,
        key: `${agg.reducer}(${agg.target}.${agg.attribute})`,
        title: agg.attribute,
        reducer: agg.reducer,
      })),
      ...(context.params.state.formulas || []).map((formula) => ({
        type: "value" as const,
        field: formula.name,
        key: formula.name,
        title: formula.name,
        reducer: "formula",
        formula: formula.formula,
      })),
    ],
    columnInfo: aggregations.data?.meta?.columnInfo || [],
    measuresAsRows: pivotGroupIndex !== undefined,
  };

  const rollupMeasureKey =
    config.rollupMeasureKey && context.view.config?.layout !== "nested"
      ? pivotConfig.aggregations.find(
          (agg) =>
            (agg.title || agg.field.split(".")[1]) === config.rollupMeasureKey
        )?.key
      : undefined;

  const { processedData, expandedRows, toggleExpanded, columnKeys } =
    usePivotTableData(aggregations.data?.data || [], {
      ...pivotConfig,
      rollupMeasureKey,
    });

  // Filter rows when in pivot mode and columns are selected
  const filteredData = useMemo(() => {
    const selectedColumns = config.columns;
    const isPivoted = config.pivotGroupIndex !== undefined;

    if (!selectedColumns?.length || !isPivoted) {
      return processedData;
    }

    return processedData.filter((row) => {
      // Keep measure rows if they're in selected columns
      if (row.field?.measureKey) {
        return selectedColumns.includes(row.field.measureKey);
      }

      // Keep parent rows if they have any selected measures as children
      if (row.children?.length) {
        return row.children.some(
          (child) =>
            child.field?.measureKey &&
            selectedColumns.includes(child.field.measureKey)
        );
      }

      // Keep all other rows (e.g., group rows)
      return true;
    });
  }, [processedData, config.columns, config.pivotGroupIndex]);

  // Sort parent rows by their aggregated values
  const sortedData = useMemo(() => {
    if (!config.enableSorting || !config.sortBy || !config.sortDirection) {
      return filteredData;
    }

    // Group rows into parent-child units
    const groups: Array<{ parent: any; children: any[] }> = [];
    let currentGroup: { parent: any; children: any[] } | null = null;

    for (const row of filteredData) {
      if (row.depth === 0 || (row.children && row.children.length > 0)) {
        // This is a parent row
        currentGroup = { parent: row, children: [] };
        groups.push(currentGroup);
      } else if (currentGroup) {
        // This is a child row
        currentGroup.children.push(row);
      }
    }

    // Sort groups by parent row's aggregated measure values
    const direction = config.sortDirection === "ascending" ? 1 : -1;

    // Find the aggregation configuration for the sort column
    const sortAggregation = pivotConfig.aggregations.find(
      (agg) =>
        agg.key === config.sortBy ||
        agg.field === config.sortBy ||
        config.sortBy === "value"
    );
    const aggregateFunction = sortAggregation?.reducer || "sum";

    groups.sort((groupA, groupB) => {
      // Helper function to get the sort value for a parent row
      const getSortValue = (parent: any) => {
        // If the parent has children, try multiple rollup value sources
        if (parent.children?.length > 0) {
          // Try rollupDetails first (most likely location) - use correct aggregate function
          if (
            parent.rollupDetails?.[config.sortBy!]?.[aggregateFunction] !==
            undefined
          ) {
            return Number(
              parent.rollupDetails[config.sortBy!][aggregateFunction]
            );
          }
          // Fallback: try rollupDetails with sum for backwards compatibility
          if (parent.rollupDetails?.[config.sortBy!]?.sum !== undefined) {
            return Number(parent.rollupDetails[config.sortBy!].sum);
          }
        }
        // Otherwise, use the direct measure value
        return Number(parent.measures?.[config.sortBy!]) || 0;
      };

      const aValue = getSortValue(groupA.parent);
      const bValue = getSortValue(groupB.parent);

      return (aValue - bValue) * direction;
    });

    // Rebuild flat array preserving parent-child structure
    return groups.flatMap((group) => [group.parent, ...group.children]);
  }, [
    filteredData,
    config.sortBy,
    config.sortDirection,
    config.enableSorting,
    pivotConfig.aggregations,
  ]);

  const shouldFlatten =
    pivotConfig.aggregations.length === 1 &&
    (context.params.state.groupBys?.length || 0) === 2;

  const columns = buildColumns(
    columnKeys,
    config,
    pivotConfig.aggregations,
    pivotConfig.columnGroups[0],
    pivotConfig.columnInfo,
    context,
    shouldFlatten,
    rollupMeasureKey
  );

  if (aggregations.isLoading) {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingOutlined />
      </div>
    );
  }

  return aggregations.data?.data?.length ? (
    <DynamicCanvas>
      {({ height }) => (
        <div
          className="compact-table"
          style={{
            height: height,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <TableHeaderProvider>
            <Table
              className={styles.compactTable}
              components={{ header: { cell: TableHeaderCustomComponent } }}
              size="small"
              columns={columns}
              dataSource={sortedData}
              rowClassName={(record) =>
                expandedRows.has(record.key) ? styles.expandedRow : ""
              }
              expandable={{
                expandedRowKeys: [...expandedRows],
                rowExpandable: (record) =>
                  !shouldFlatten &&
                  record.children !== undefined &&
                  record.children.length > 0,
                onExpand: (expanded, record) => toggleExpanded(record.key),
                expandRowByClick: true,
              }}
              pagination={{
                pageSize: config.autoPageSize
                  ? Math.floor((height - (26 + 33)) / 41)
                  : 20,
                hideOnSinglePage: true,
                size: "small",
                style: { margin: "8px 0 0", height: 25, overflow: "visible" },
              }}
              scroll={{
                x: "max-content",
                y: height - 90,
              }}
              sticky
              style={{
                height: "100%",
                overflow: "auto",
              }}
            />
          </TableHeaderProvider>
        </div>
      )}
    </DynamicCanvas>
  ) : aggregations.data?.meta?.explanation ? (
    <EmptyResultExplanation explanation={aggregations.data.meta.explanation} />
  ) : null;
}

export function PivotTableV2Controls(props: {
  context: VisualizationProps["context"];
  controlsConfig?: PivotTableV2ControlsConfig;
}) {
  const { view, params, aggregations: aggr } = props.context;
  const {
    showGroupSelector = true,
    showPaging = true,
    showWrapping = true,
    showCompare = true,
    showLayoutSelector = true,
    showColumnSelector = true,
    showRollupOptions = true,
    showRollupMeasureSelector = true,
    showSortingControls = true,
  } = props.controlsConfig || {};

  const config = {
    ...view.config,
    ...toCompareWithConfig(view.config),
    ...toFormattingConfig(view.config),
    autoPageSize: view.config?.autoPageSize ?? true,
    allowWrap: view.config?.allowWrap ?? false,
    pivotGroupIndex: view.config?.pivotGroupIndex,
    rollupMeasureKey: view.config?.rollupMeasureKey,
    columns: view.config?.columns || [],
    // Sorting configuration
    enableSorting: view.config?.enableSorting ?? true,
    sortBy: view.config?.sortBy,
    sortDirection: view.config?.sortDirection,
  };

  // Get all available columns
  const allColumns = useMemo(() => {
    return buildAllColumns(params.state.aggregations, params.state.formulas);
  }, [params.state.aggregations, params.state.formulas]);

  // Memoized stats options to prevent stale closures
  const statsOptions = useMemo(
    () =>
      ["min", "max", "sum", "avg"].map((stat) => ({
        label: stat,
        value: stat,
      })),
    []
  );

  // Debounce column changes
  const updateColumns = useCallback(
    (newColumns: string[]) => {
      view.setConfig({ ...config, columns: newColumns });
    },
    [config, view]
  );

  const debouncedUpdateColumns = useMemo(
    () => debounce(updateColumns, 300),
    [updateColumns]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateColumns.cancel();
    };
  }, [debouncedUpdateColumns]);

  // Handle layout changes
  const handleLayoutChange = (
    layout: "flat" | "pivot" | "nested",
    contentView: ContentViews,
    newConfig: any = {}
  ) => {
    view.set(contentView);
    view.setConfig({ ...view.config, ...newConfig, layout });
  };

  const diffMethod = props.context.params.state.compareWith?.method || "";
  const hasCompare = diffMethod === "tuple";
  const groupBys = props.context.params.state.groupBys || [];
  const aggregations = props.context.params.state.aggregations || [];
  // find the aggregation that matches the rollup measure key
  const rollupMeasureKey = aggregations.find(
    (agg) => agg.attribute === config.rollupMeasureKey
  )?.attribute;
  // Column selector is now supported in pivot mode
  const canShowColumnSelector = showColumnSelector;
  // Check if the data has time units
  const hasTime = hasTimeUnit(props.context.aggregations);

  return (
    <div>
      {showLayoutSelector && (
        <TableLayoutSelector
          currentLayout={config.layout || "p2"}
          onLayoutChange={handleLayoutChange}
          context={props.context}
        />
      )}

      {canShowColumnSelector && (
        <div style={{ marginTop: 8 }}>
          <TableColumns
            selectedColumns={config.columns}
            defaultColumns={allColumns}
            allColumns={allColumns}
            columnFormulaNames={params.state.formulas?.map((f) => f.name) || []}
            columnInfo={aggr.data?.meta?.columnInfo || []}
            onSelectColumn={(column) => {
              const newColumns = [...config.columns];
              if (!newColumns.includes(column)) {
                newColumns.push(column);
                debouncedUpdateColumns(newColumns);
              }
            }}
            onRemoveColumn={(column) => {
              const newColumns = config.columns.filter(
                (c: string) => c !== column
              );
              view.setConfig({ ...config, columns: newColumns });
            }}
            onReorderColumns={(newColumns) => {
              debouncedUpdateColumns(newColumns);
            }}
          />
        </div>
      )}

      {showGroupSelector && (
        <div className="section">
          <div className="section-header">Pivot Options</div>
          <Flex start direction="column">
            <div style={{ width: "100%", marginBottom: 8 }}>
              <Flex spaceBetween style={{ width: "100%" }}>
                <span className="viz-label">Pivot By Group</span>
                <Select
                  value={config.pivotGroupIndex ?? ""}
                  onChange={(value) => {
                    view.setConfig({
                      ...config,
                      pivotGroupIndex: value === "" ? undefined : value,
                    });
                  }}
                  style={{ width: 200 }}
                  options={groupBys.map((group, index) => ({
                    label: group.attribute,
                    value: index,
                  }))}
                  bordered={false}
                  className="viz-select"
                />
              </Flex>
            </div>
          </Flex>
        </div>
      )}

      <div className="section">
        <div className="section-header">Display Options</div>
        <Flex start direction="column" style={{ width: "100%" }}>
          {showCompare && hasCompare && (
            <CompareWithControls
              disabled={!hasCompare}
              changeColor={config.changeColor}
              showChangeAs={config.showChangeAs}
              showChangeValues={config.showChangeValues}
              onChangeShowChangeAs={(showChangeAs) => {
                view.setConfig({ ...config, showChangeAs });
              }}
              onChangeShowChangeValues={(showChangeValues) => {
                view.setConfig({ ...config, showChangeValues });
              }}
              onChangeColor={(changeColor) => {
                view.setConfig({ ...config, changeColor });
              }}
            />
          )}

          <div style={{ width: "100%", marginBottom: 8 }}>
            <Flex spaceBetween style={{ width: "100%" }}>
              <span className="viz-label">Show Rollups</span>
              <Switch
                checked={config.showRollups ?? false}
                onChange={(checked) => {
                  view.setConfig({ ...config, showRollups: checked });
                }}
                size="small"
              />
            </Flex>
          </div>

          {showWrapping && (
            <div style={{ width: "100%", marginBottom: 8 }}>
              <Flex spaceBetween style={{ width: "100%" }}>
                <span className="viz-label">Allow wrapping</span>
                <Switch
                  checked={config.allowWrap}
                  onChange={(checked) => {
                    view.setConfig({ ...config, allowWrap: checked });
                  }}
                  size="small"
                />
              </Flex>
            </div>
          )}

          {showPaging && (
            <div style={{ width: "100%", marginBottom: 8 }}>
              <Flex spaceBetween style={{ width: "100%" }}>
                <span className="viz-label">Adaptive Paging</span>
                <Switch
                  disabled={config.allowWrap}
                  checked={!config.allowWrap && config.autoPageSize}
                  onChange={(checked) => {
                    view.setConfig({ ...config, autoPageSize: checked });
                  }}
                  size="small"
                />
              </Flex>
            </div>
          )}

          {hasTime && (
            <div style={{ width: "100%" }}>
              <FormattingControls
                disabled={!hasTime}
                timeUnit={config.timeUnit}
                onChangeTimeUnit={(timeUnit) =>
                  props.context.view.setConfig({ ...config, timeUnit })
                }
              />
            </div>
          )}

          {/* Sorting Controls */}
          {showSortingControls && (
            <>
              <div style={{ width: "100%", marginBottom: 8 }}>
                <Flex spaceBetween style={{ width: "100%" }}>
                  <span className="viz-label">Enable Sorting</span>
                  <Switch
                    checked={config.enableSorting}
                    onChange={(checked) => {
                      view.setConfig({
                        ...config,
                        enableSorting: checked,
                        sortBy: checked ? "value" : undefined,
                        sortDirection: checked ? "ascending" : undefined,
                      });
                    }}
                    size="small"
                  />
                </Flex>
              </div>

              {config.enableSorting && (
                <div style={{ width: "100%", marginBottom: 8 }}>
                  <Flex spaceBetween style={{ width: "100%" }}>
                    <span className="viz-label">Sort Direction</span>
                    <Select
                      value={config.sortDirection || "ascending"}
                      onChange={(direction) => {
                        view.setConfig({
                          ...config,
                          sortDirection: direction,
                          sortBy: "value",
                        });
                      }}
                      style={{ width: 120 }}
                      options={[
                        { label: "Ascending", value: "ascending" },
                        { label: "Descending", value: "descending" },
                      ]}
                      bordered={false}
                      className="viz-select"
                    />
                  </Flex>
                </div>
              )}
            </>
          )}
        </Flex>
      </div>

      {showRollupOptions && (
        <div className="section">
          <div className="section-header">Rollup Options</div>
          <Flex start direction="column">
            {showRollupMeasureSelector && (
              <div style={{ width: "100%", marginBottom: 8 }}>
                <Flex spaceBetween style={{ width: "100%" }}>
                  <span className="viz-label">Measure</span>
                  <Select
                    value={config.rollupMeasureKey ?? ""}
                    onChange={(value) => {
                      view.setConfig({
                        ...config,
                        rollupMeasureKey: value === "" ? undefined : value,
                      });
                    }}
                    style={{ width: 150 }}
                    options={[
                      ...aggregations.map((agg) => ({
                        label: agg.attribute,
                        value: agg.attribute,
                      })),
                      ...(params.state.formulas || []).map((formula) => ({
                        label: formula.name,
                        value: formula.name,
                      })),
                    ]}
                    bordered={false}
                    className="viz-select"
                  />
                </Flex>
              </div>
            )}
            <div style={{ width: "100%", marginBottom: 8 }}>
              <Flex spaceBetween style={{ width: "100%" }}>
                <span className="viz-label">Stats Value</span>
                <Select
                  key={`stats-selector-${config.selectedRollupStats}`}
                  value={config.selectedRollupStats ?? ""}
                  placeholder="Select stats value"
                  allowClear
                  virtual={false}
                  onChange={(value) => {
                    view.setConfig({
                      ...config,
                      selectedRollupStats: value === "" ? undefined : value,
                    });
                  }}
                  style={{ width: 150 }}
                  options={statsOptions}
                  bordered={false}
                  className="viz-select"
                />
              </Flex>
            </div>
          </Flex>
        </div>
      )}
    </div>
  );
}
